import { useEffect, useState } from 'react';
import { CompanyService } from '../../services/companyService';
import featureService from '../../services/featureService';
import UsersService, { User } from '../../services/usersService';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  setUserEmail,
  setUserCompanyData,
  setUserFeatures,
  setUser,
  setAuthed,
  setGetAccessToken,
} from '../../store/adminShell';
import { useAuth0 } from '@auth0/auth0-react';

export const useLoadData = () => {
  const dispatch = useAppDispatch();
  const getUserActiveFeatures = async (email: string) =>
    dispatch(setUserFeatures(await featureService.getUserFeatures(email)));
  const { getAccessTokenSilently, isAuthenticated, getIdTokenClaims, isLoading: isLoadingAuth0 } = useAuth0();
  const getAccessToken = useAppSelector((state) => state.adminShell.getAccessToken);
  const userFeatures = useAppSelector((state) => state.adminShell.userFeatures);
  const authed = useAppSelector((state) => state.adminShell.authed);
  const [isLoading, setIsLoading] = useState(true);
  const [authFailed, setAuthFailed] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(setGetAccessToken(getAccessTokenSilently));
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const handleLoadData = async () => {
      try {
        const claims = await getIdTokenClaims();
        const email = claims?.upn || claims?.email;
        if (!email) throw new Error('Auth0 session has no email');

        dispatch(setUserEmail(email));
        await fetchUserInformation(email);

        // Remove token and email url params after committing to state
        const url = new URL(window.location.href);
        url.searchParams.delete('token');
        url.searchParams.delete('email');
        window.history.replaceState(null, '', url);
        setIsLoading(false);
      } catch (error) {
        setAuthFailed(true);
      }
    };
    const getUserCompanyInfo = async (user: User) => {
      try {
        const companyService = new CompanyService();
        const [firstCompany] = await companyService.getCompanyByEmail(user.email);
        dispatch(setUserCompanyData(firstCompany));
      } catch (error) {
        console.error('Failed to get users company information.', error);
      }
    };

    const getUserInfo = async (user: User) => {
      try {
        const userPermissions = user.permissions.map((permission) => permission.name);
        dispatch(setUser({ ...user, permissions: userPermissions }));
        await getUserActiveFeatures(user.email);
        dispatch(setAuthed('user'));
      } catch (error) {
        setAuthFailed(true);
      }
    };

    const fetchUserInformation = async (email: string) => {
      const usersService = new UsersService();
      const { user } = await usersService.getUserByEmail(email);
      await getUserInfo(user);
      await getUserCompanyInfo(user);
    };
    if (userFeatures.length > 0 && getAccessToken && !authed && !isLoadingAuth0 && isAuthenticated) {
      handleLoadData();
    }
  }, [userFeatures, getAccessToken, authed, isLoadingAuth0, isAuthenticated]);

  return {
    authFailed,
    isLoading,
  };
};
